import { Box, MenuItem, Select, Typography } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { generate12HourOptions, menuPropsModal, to12h, to24h } from "../../utils";
import { PreciseTimePicker } from "./PreciseTimePicker";
import { EArrivalTimeType } from "./ArrivalDateTime/Container";

interface TimePickerComponentProps {
  onChange: (newTime: DateTime) => void;
  label?: string;
  iconColor?: "grey" | "orange" | "colorized";
  showLabel?: boolean;
  fullWidth?: boolean;
  modalKey?: string;
  initialTime?: DateTime;
  type?: EArrivalTimeType;
  disabled?: boolean;
  minuteStep?: number;
}

/**
 * Normalize DateTime by setting seconds and milliseconds to 0
 */
const normalizeDateTime = (dt: DateTime): DateTime => {
  return dt.set({ second: 0, millisecond: 0 });
};

/**
 * TimePickerComponent component
 * It is used to select a time from a list of 12-hour options and a precise time picker. Data is stored in the internalTime state with format HH:mm AM/PM, example: 12:00 PM.
 * @param onChange - Function to call when the time changes: Callback Arguments: onChange(newTime: DateTime) => void. Use this function to update the time in the parent component.
 * @param label - Label to display (string) Default: "At" if showLabel is true
 * @param iconColor - Color of the icon (grey, orange, colorized) Default: orange
 * @param showLabel - Whether to show the label (boolean)
 * @param fullWidth - Whether to make the component full width (boolean)
 * @param modalKey - Key to use for the modal (string)
 * @param initialTime - Initial time to use (DateTime) Default: current time
 * @param type - Type of time picker (exact, window) Default: exact
 * @returns TimePickerComponent component (Box with label, Select, and PreciseTimePicker)
 */
export const TimePickerComponent = ({
  onChange,
  label = "At",
  iconColor = "orange",
  showLabel = true,
  fullWidth = true,
  modalKey,
  disabled = false,
  initialTime,
  type,
  minuteStep,
}: TimePickerComponentProps) => {
  const selectMinuteStep = minuteStep ?? 10;
  const [internalTime, setInternalTime] = useState<DateTime>(() => {
    const baseTime = initialTime?.isValid ? initialTime : DateTime.now();
    return normalizeDateTime(baseTime);
  });

  // Track last user-initiated time change to prevent sync loops
  const lastUserChangeRef = useRef<string | null>(null);
  // Track if current time comes from PreciseTimePicker (exact time, not in Select options)
  const isPreciseTimeRef = useRef<boolean>(false);

  const time24 = useMemo(() => {
    if (!internalTime.isValid) return "00:00";
    return internalTime.toFormat("HH:mm");
  }, [internalTime]);

  // Generate Select options, including precise time if it doesn't exist in base options
  const selectOptions = useMemo(() => {
    const baseOptions = generate12HourOptions(selectMinuteStep);

    if (!internalTime.isValid) return baseOptions;

    try {
      const time12 = to12h(time24);

      // If exact time already exists in options, return base options
      if (baseOptions.includes(time12)) {
        return baseOptions;
      }

      // Otherwise, insert the precise time in the correct position
      const currentMinutes = internalTime.hour * 60 + internalTime.minute;
      const optionsWithPrecise = [...baseOptions];

      // Find insertion point (sorted chronologically)
      let insertIndex = 0;
      for (let i = 0; i < baseOptions.length; i++) {
        const option24 = to24h(baseOptions[i]);
        const [hour, minute] = option24.split(":").map(Number);
        const optionMinutes = hour * 60 + minute;

        if (currentMinutes > optionMinutes) {
          insertIndex = i + 1;
        } else {
          break;
        }
      }

      optionsWithPrecise.splice(insertIndex, 0, time12);
      return optionsWithPrecise;
    } catch {
      return baseOptions;
    }
  }, [internalTime, time24, selectMinuteStep]);

  const selectValue = useMemo(() => {
    if (!internalTime.isValid) return "";
    try {
      return to12h(time24);
    } catch {
      return "";
    }
  }, [internalTime, time24]);

  const handleSelectChange = (val12h: string) => {
    const val24 = to24h(val12h);
    const [hour, minute] = val24.split(":").map(Number);

    const newTime = normalizeDateTime(internalTime.set({ hour, minute }));

    if (!newTime.isValid) return;

    // Check if selected time exists in base options (not a dynamically added precise time)
    const time12 = to12h(val24);
    const baseOptions = generate12HourOptions(selectMinuteStep);
    const isBaseOption = baseOptions.includes(time12);

    // Update precise flag based on whether it's a base option
    isPreciseTimeRef.current = !isBaseOption;

    lastUserChangeRef.current = newTime.toISO();
    setInternalTime(newTime);
    onChange(newTime);
  };

  const handlePreciseTimeChange = (newTime: DateTime) => {
    if (!newTime.isValid) return;

    // Preserve exact minutes from PreciseTimePicker (no normalization)
    const updatedTime = normalizeDateTime(newTime);

    if (updatedTime.isValid) {
      // Check if this time exists in Select options
      const time12 = to12h(updatedTime.toFormat("HH:mm"));
      const options = generate12HourOptions(selectMinuteStep);
      const exactTimeExists = options.includes(time12);

      // Mark as precise time if it doesn't exist in options
      isPreciseTimeRef.current = !exactTimeExists;

      lastUserChangeRef.current = updatedTime.toISO();
      setInternalTime(updatedTime);
      onChange(updatedTime);
    }
  };

  // Sync internalTime with initialTime from parent, but skip if it's a user-initiated change
  useEffect(() => {
    if (!initialTime?.isValid) return;

    const normalized = normalizeDateTime(initialTime);
    const normalizedISO = normalized.toISO();

    // Skip sync if this is the same time we just set from user action
    if (normalizedISO === lastUserChangeRef.current) {
      lastUserChangeRef.current = null;
      return;
    }

    setInternalTime((currentTime) => {
      if (normalized.hour !== currentTime.hour || normalized.minute !== currentTime.minute) {
        // Check if synced time exists in Select options
        const time12 = to12h(normalized.toFormat("HH:mm"));
        const options = generate12HourOptions(selectMinuteStep);
        isPreciseTimeRef.current = !options.includes(time12);
        return normalized;
      }
      return currentTime;
    });
  }, [initialTime, selectMinuteStep]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box sx={{ 
        display: "flex", 
        gap: { xs: 0.5, sm: 1 }, 
        alignItems: "center",
        width: fullWidth ? "100%" : "auto",
        minWidth: 0,
        maxWidth: "100%",
      }}>
        {showLabel && (
          <Typography
            sx={{
              fontSize: { xs: 10, sm: 12 },
              color: "var(--fx-surface-textsecondary, #64748B)",
              minWidth: { xs: 36, sm: 44 },
              flexShrink: 0,
              textAlign: "center",
            }}
          >
            {label}
          </Typography>
        )}

        <Select
          sx={{ 
            minWidth: fullWidth ? "auto" : { xs: 80, sm: 100 },
            flex: fullWidth ? 1 : "0 1 auto",
            maxWidth: "100%",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            "& .MuiSelect-select": {
              py: { xs: 0.75, sm: 1 },
            },
          }}
          MenuProps={menuPropsModal}
          fullWidth={fullWidth}
          size="small"
          disabled={disabled}
          value={selectValue}
          onChange={(e) => handleSelectChange(e.target.value)}
        >
          {selectOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ flexShrink: 0, overflow: "visible" }}>
          <PreciseTimePicker
            disabled={disabled}
            iconColor={iconColor}
            value={internalTime}
            onChange={handlePreciseTimeChange}
            modalKey={modalKey ?? `time-picker-${time24}`}
            minutesStep={minuteStep}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
